import { useMutation } from 'react-query'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import Bugsnag from '@bugsnag/js'

import { Box, Link, Typography } from '@mui/material'

import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'
import { checkEmail } from 'services/api/signup'
import Astronaut from '../assets/astronaut.svg'
import { useFormik } from 'formik'
import * as yup from 'yup'
import AdxButton from 'components/custom/AdxButton'

const SignUp = () => {
  const navigate = useNavigate()

  const validationSchema = yup.object({
    email: yup.string().email('Invalid Email Address').nullable().required('Email is required'),
  })

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: (values) => {
      checkEmailQuery(values)
    },
  })

  // react query mutate hook
  const { isLoading: checkEmailLoading, mutate: checkEmailQuery } = useMutation(
    (email) => checkEmail(email),
    {
      onSuccess: async ({ data }) => {
        if (data.status_code) {
          if (data.status_code === 'SF') {
            formik?.setFieldError(
              'email',
              'Email has been used to register adnexio HR Tech. Please use another email.'
            )
          } else {
            navigate('/signup/password', {
              state: {
                email: formik?.values?.email,
                accountType: data.status_code,
                fromCheckEmail: true,
              },
            })
          }
        } else {
          navigate('/signup/register', {
            state: {
              email: formik?.values?.email,
              fromCheckEmail: true,
            },
          })
        }
      },
      onError: async (err) => {
        formik?.setFieldError('email', err?.data?.message)
        Bugsnag.notify(err)
      },
    }
  )

  const handleNavigateHome = () => {
    window.scrollTo(0, 0)
    navigate('/')
  }

  return (
    <OnboardingContainer
      bgColorRight="alternate.main"
      topRightPhoto="10%"
      leftRightPhoto="75%"
      heightRightPhoto="100%"
      transformRightPhoto={`translate(${-50}%, ${0}%)`}
    >
      <Box
        height={'100%'}
        width={'100%'}
        display="flex"
        justifyContent={{ xs: 'center', lg: 'flex-end' }}
        alignItems={{ xs: 'center', lg: 'center' }}
      >
        <form
          onSubmit={formik.handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            width={{ xs: '100%', lg: '500px' }}
            height={{ xs: '90vh', lg: '500px' }}
            p={{ xs: 2, lg: 0 }}
            position={'relative'}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            margin="0 auto"
          >
            <Box display={'flex'} flexDirection={'column'} gap={2}>
              <Box
                component={'img'}
                src={'/adnexio-logo-text.svg'}
                alt="adnexio logo"
                sx={{ cursor: 'pointer' }}
                width={160}
                height={32}
                display={{ xs: 'block', lg: 'none' }}
              />
              <Box>
                <Typography
                  variant="h1"
                  mb={1}
                  sx={{ fontWeight: 700 }}
                  lineHeight={{ xs: '38px', lg: '50px' }}
                >
                  Onboard your organization
                </Typography>
                <Typography variant="body1" textAlign="justify" color="light-gray-text.main">
                  Enter your email address below. We will check if you already have an account.
                </Typography>
              </Box>
              <AdxInput
                label="Email"
                placeholder="admin@company.com"
                value={formik?.values?.email}
                name="email"
                onChange={formik.handleChange}
                isErrorShown={formik?.touched?.email && formik?.errors?.email}
                errorMessage={<AdxErrorMessage>{formik?.errors?.email}</AdxErrorMessage>}
              />
            </Box>
            <Box>
              <AdxButton
                isLoading={checkEmailLoading}
                disabled={checkEmailLoading}
                type="submit"
                sx={{ width: '100%', mb: 2 }}
              >
                Continue
              </AdxButton>
              <Typography variant="body1" textAlign="start">
                Organization already existed? Sign in as employee{' '}
                <Link
                  component={RouterLink}
                  to={'/login'}
                  style={{ textDecoration: 'none' }}
                  sx={{ color: 'alternate.main' }}
                >
                  here
                </Link>
              </Typography>
            </Box>
          </Box>
        </form>
      </Box>
    </OnboardingContainer>
  )
}

export default SignUp
